import { TextField } from "@faro-lotv/flat-ui";
import { MAX_NAME_LENGTH } from "@faro-lotv/ielement-types";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { useIsAnnotationEditorDark } from "./annotation-editor-surface-context";

export type AnnotationEditorHeaderProps = {
  /** Current value of the title text field */
  title?: string;

  /**
   * If true the title input field will be focused and its text will be selected, when the form is first shown.
   *
   * @default false
   */
  autoFocusTitle?: boolean;

  /** Ref to the input element */
  inputRef?: React.RefObject<HTMLInputElement | null>;

  /** Function called when the content of the title text field changes */
  onTitleChange(title: string): void;

  /** Error message to show below edit title control */
  errorMessage?: string;

  /** Function to inform parent component about error condition in control */
  onSetErrorMessage?(error?: string): void;

  /** if `true` the component will be disabled */
  disabled: boolean;
};

/**
 * @returns the UI control for getting the title of an annotation
 */
export function AnnotationEditorHeader({
  // The default value is set to avoid warning logs that appear when going from undefined to a string
  // The assigned value it's a specific value used by the Dropdown Api
  title = "",
  autoFocusTitle,
  onTitleChange,
  errorMessage,
  onSetErrorMessage,
  disabled,
  inputRef,
}: AnnotationEditorHeaderProps): JSX.Element {
  const isDark = useIsAnnotationEditorDark();
  const [isFirstFocus, setIsFirstFocus] = useState(true);

  const onTextChanged = useCallback(
    (text: string) => {
      onTitleChange(text);

      if (onSetErrorMessage) {
        onSetErrorMessage(
          text.length > MAX_NAME_LENGTH
            ? "Maximum character limit exceeded"
            : undefined,
        );
      }
    },
    [onTitleChange, onSetErrorMessage],
  );

  const localInputRef = useRef<HTMLInputElement>(null);
  const finalInputRef = inputRef ?? localInputRef;

  // Focus the title input field when the form is first shown
  useEffect(() => {
    if (!finalInputRef.current || !autoFocusTitle) return;
    finalInputRef.current.focus();
  }, [autoFocusTitle, finalInputRef]);

  return (
    <TextField
      inputRef={finalInputRef}
      disabled={disabled}
      text={title}
      fullWidth
      variant="outlined"
      label="Title"
      tag="star"
      error={errorMessage}
      dark={isDark}
      placeholder="Insert a title"
      sx={{
        height: "fit-content",
        minHeight: "32px",
        input: { fontSize: "1rem" },
      }}
      onTextChanged={onTextChanged}
      maxCharacterCount={MAX_NAME_LENGTH}
      shouldShowCounter
      multiline
      inputProps={{ style: { fontSize: "1rem" } }}
      onFocus={(e) => {
        // Select the text on the first focus
        if (isFirstFocus && autoFocusTitle) {
          e.target.select();
          setIsFirstFocus(false);
          if (onSetErrorMessage) onSetErrorMessage(undefined);
        }
      }}
      onBlur={() => {
        if (title.trim().length === 0) {
          if (onSetErrorMessage) onSetErrorMessage("Title is required");
        }
      }}
    />
  );
}
